import type { IconName } from '@customerio/pluma-icons';
import { z } from '@customerio/hydra-validation';
import { isEmptyString } from '@/utils/string';
import type { Delivery, DeliveryDetail, DeliveriesListParams, DeliveryType } from './entity';

/**
 * Pure delivery state-derivation + request-shaping helpers, ported from the
 * Ember `app/models/delivery.ts` and `app/controllers/deliveries.js`.
 *
 * **The order of the state arrays is load-bearing**: to determine the current
 * delivery state we walk an array and take the first "present" timestamp.
 *
 * "Present" = a positive unix-second timestamp. The wire sends `null`
 * (sometimes `0`) for absent states; we treat both as absent, matching the
 * Ember model's truthiness-based `deliveryState` find (and avoiding the latent
 * `isPresent(0)` edge in `_latestChronologicalState`, immaterial for real data).
 */

const STATES = [
  'holdout',
  'canceled',
  'spammed',
  'unsubscribed',
  'converted',
  'replied',
  'clicked',
  'human_clicked',
  'machine_clicked',
  'opened',
  'human_opened',
  'prefetch_opened',
  'suppressed',
  'bounced',
  'delivered',
  'deferred',
  'sent',
  'failed',
  'undeliverable',
  'attempted',
  'drafted',
  'queued',
] as const;

const DELIVERY_STATES = [
  'holdout',
  'canceled',
  'spammed',
  'suppressed',
  'bounced',
  'delivered',
  'sent',
  'failed',
  'undeliverable',
  'deferred',
  'attempted',
  'drafted',
  'queued',
] as const;

const END_USER_STATES = ['spammed', 'unsubscribed', 'converted', 'replied', 'clicked', 'opened'] as const;

const END_USER_STATES_EMAIL = [
  'spammed',
  'unsubscribed',
  'converted',
  'replied',
  'human_clicked',
  'machine_clicked',
  'clicked',
  'human_opened',
  'prefetch_opened',
  'opened',
] as const;

const CHRONOLOGICAL_STATES = [
  'holdout',
  'canceled',
  'suppressed',
  'bounced',
  'delivered',
  'deferred',
  'failed',
  'undeliverable',
  'attempted',
  'drafted',
  'queued',
] as const;

type DeliveryState = (typeof STATES)[number];

/** State names whose value comes from the `metrics` map, not a top-level attr. */
const HUMAN_METRIC_STATES = new Set(['human_opened', 'human_clicked', 'prefetch_opened', 'machine_clicked']);

/** Email-channel deliveries expose the human/machine breakdown. */
export function isEmail(d: Pick<Delivery, 'action_type'>): boolean {
  return d.action_type === 'email_action';
}

/** Push `recipient` values that name a device selection rather than a person. */
const PUSH_RESERVED_RECIPIENTS = new Set(['all', 'last_used']);

/** The recipient as one plain string (raw, or JSON-encoded), or `null` when it is JSON of another shape, like a token array. */
function plainRecipient(recipient: string): string | null {
  try {
    const parsed: unknown = JSON.parse(recipient);
    return typeof parsed === 'string' ? parsed : null;
  } catch {
    return recipient;
  }
}

/**
 * The person a delivery went to, for display. Ported from the Ember model's
 * `pushRecipient`: a push delivery's `recipient` is usually a JSON array of
 * device tokens (or a reserved selection like `"all"`), so it shows the person's
 * name or id instead, and "deleted profile" when the person is gone. A push sent
 * to a plain address keeps it. Every other channel shows `recipient` as is.
 */
export function displayRecipient(
  d: Pick<Delivery, 'action_type' | 'recipient' | 'customer_id' | 'customer_name'>,
): string {
  if (d.action_type !== 'push_action') return d.recipient;

  const plain = plainRecipient(d.recipient);
  if (plain !== null && !PUSH_RESERVED_RECIPIENTS.has(plain)) return plain;

  if (d.customer_id === null || d.customer_id === undefined) return 'deleted profile';
  // Ember's `customerName || customerId`: only a missing or empty name falls back, not a blank one.
  const name = d.customer_name ?? '';
  return name.length > 0 ? name : d.customer_id;
}

/**
 * Safely read a key from a delivery's raw `meta` string-map. The wire always
 * populates `meta` (services `build_model.go` initializes it), so the spec's
 * `required` is correct; the guard here is belt-and-suspenders for
 * non-server-shaped `Delivery`s (test fixtures / partial cache) that omit it.
 */
export function metaValue(d: Pick<Delivery, 'meta'>, key: string): string | undefined {
  const meta: Record<string, string | undefined> | undefined = d.meta;
  return meta?.[key];
}

/**
 * One device's push delivery status, parsed out of the stringified JSON array
 * in `meta.reason` (Ember `PushDeliveryMetaReason`). `status === 200` is a
 * successful send; `bounced` / `suppressed` / `undeliverable` classify the rest,
 * and anything left over is treated as failed. Fields are validated leniently
 * (each carries a `.catch`) so a malformed device entry degrades to defaults
 * rather than dropping the whole array.
 */
const PushDeliveryMetaReasonSchema = z.object({
  id: z.string().catch(''),
  platform: z.string().catch(''),
  app_id: z.string().optional().catch(undefined),
  status: z.coerce.number().catch(0),
  detail: z.string().catch(''),
  bounced: z.boolean().catch(false),
  suppressed: z.boolean().catch(false),
  undeliverable: z.boolean().optional().catch(undefined),
  /** What the provider said about this device, verbatim (web push stores it here, not under `meta`). */
  full_error_message: z.string().optional().catch(undefined),
  /** The id of the notification button the person clicked (web push). */
  action: z.string().optional().catch(undefined),
});

export type PushDeliveryMetaReason = z.infer<typeof PushDeliveryMetaReasonSchema>;

/** Non-array / malformed payloads collapse to `[]` (Ember's `try/catch` returning null). */
const DeviceStatusesSchema = z.array(PushDeliveryMetaReasonSchema).catch([]);

// `web` must match the push-credentials registry (`PUSH_PLATFORMS.web.icon`), the one Web mark
// used everywhere; a test pins the two together.
const PUSH_PLATFORM_ICONS = new Map<string, IconName>([
  ['web', 'browser'],
  ['ios', 'apple'],
  ['android', 'android'],
]);

/** The icon a push device's platform is drawn with (Ember `push-platform-icon`); an unknown one is a desktop. */
export function pushPlatformIcon(platform: string): IconName {
  return PUSH_PLATFORM_ICONS.get(platform) ?? 'desktop';
}

const PUSH_PLATFORM_LABELS = new Map<string, string>([
  ['web', 'Web'],
  ['ios', 'iOS'],
  ['android', 'Android'],
]);

/** A push device's platform as Ember labels it: "iOS", "Android", "Web"; an unknown one by its own name. */
export function pushPlatformLabel(platform: string): string {
  return PUSH_PLATFORM_LABELS.get(platform) ?? platform;
}

/**
 * The per-device push statuses for a delivery, parsed from `meta.reason` (Ember
 * `deviceStatuses`). Returns `[]` when there is no reason string, it isn't valid
 * JSON, or it isn't a JSON array — the grouped results simply render empty.
 */
export function getDeviceStatuses(d: Pick<Delivery, 'meta'>): PushDeliveryMetaReason[] {
  const reason = metaValue(d, 'reason');
  if (isEmptyString(reason)) {
    return [];
  }
  let data: unknown;
  try {
    data = JSON.parse(reason);
  } catch {
    return [];
  }
  return DeviceStatusesSchema.parse(data);
}

/**
 * Whether a delivery is a web push: a push whose every device status is a `web`
 * platform. Nothing else on a delivery names the platform, so this is the one
 * test every web-push-only treatment (list glyph, detail, timeline) shares.
 */
export function isWebPushDelivery(d: Pick<Delivery, 'action_type' | 'meta'>): boolean {
  if (d.action_type !== 'push_action') {
    return false;
  }
  const devices = getDeviceStatuses(d);
  return devices.length > 0 && devices.every((device) => device.platform === 'web');
}

/** The id of the notification button the person clicked, from the first device that recorded one. */
export function clickedPushAction(d: Pick<Delivery, 'meta'>): string | undefined {
  return getDeviceStatuses(d).find((device) => !isEmptyString(device.action))?.action;
}

/**
 * The distinct platforms a push delivery reached, sorted, derived from its
 * per-device statuses (Ember `pushPlatforms`). Drives the push preview's initial
 * platform selection; `[]` when there are no device statuses (e.g. a draft).
 */
export function getPushPlatforms(d: Pick<Delivery, 'meta'>): string[] {
  const platforms = getDeviceStatuses(d)
    .map((device) => device.platform)
    .filter((platform) => !isEmptyString(platform));
  return [...new Set(platforms)].sort();
}

/**
 * Resolve a state's timestamp value. `queued` aliases `created` (the Ember
 * `@readOnly('created')`); the human/machine metric states read the (string)
 * `metrics` map, gated on email channel; everything else is a top-level attr.
 */
function getStateTimestamp(d: Delivery, state: DeliveryState): number {
  if (state === 'queued') {
    return d.created ?? 0;
  }
  if (HUMAN_METRIC_STATES.has(state)) {
    if (!isEmail(d)) return 0;
    return Number(d.metrics?.[state] ?? 0);
  }
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  const value = (d as Record<string, unknown>)[state];
  return typeof value === 'number' ? value : 0;
}

function hasState(d: Delivery, state: DeliveryState): boolean {
  return getStateTimestamp(d, state) > 0;
}

/** First present state in `DELIVERY_STATES` priority order (or undefined). */
export function getDeliveryState(d: Delivery): DeliveryState | undefined {
  return DELIVERY_STATES.find((state) => hasState(d, state));
}

/**
 * The numeric timestamp/count backing any state name (`queued` aliases
 * `created`; the human/machine states read the email `metrics` map; everything
 * else is a top-level attr). `0` when absent or off-channel. The single source
 * of truth for the expanded row's timestamp values.
 */
export function deliveryStateValue(d: Delivery, state: string): number {
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return getStateTimestamp(d, state as DeliveryState);
}

/** A metric value further past `created` than this can't be this delivery's engagement. */
const MAX_DELIVERY_METRIC_ELAPSED_SECONDS = 30 * 24 * 60 * 60;

/**
 * Whether a value read off the opaque `metrics` map is usable as a timestamp.
 *
 * `metrics` / `archived_metrics` are string maps that mix **count**- and
 * timestamp-shaped values, so the human/machine engagement reads can't be trusted
 * as timestamps: a count of `2` would render as 1 Jan 1970. A value only counts as
 * a timestamp if it sits inside the delivery's own lifetime — anything before
 * `created`, or absurdly after it, reads as absent instead.
 */
export function isDeliveryMetricTimestamp(value: number, created: number | null | undefined): boolean {
  if (!Number.isFinite(value) || value <= 0) return false;
  if (created == null || created <= 0) return false;
  return value >= created && value <= created + MAX_DELIVERY_METRIC_ELAPSED_SECONDS;
}

/** The states whose values come from the ambiguous `metrics` map and need the bounds check. */
export function isMetricSourcedState(state: string): boolean {
  return HUMAN_METRIC_STATES.has(state);
}

/**
 * Holdout deliveries always report as `holdout`; otherwise the most recent
 * (highest timestamp) present chronological state.
 */
function getLatestChronologicalState(d: Delivery): DeliveryState | undefined {
  if (hasState(d, 'holdout')) {
    return CHRONOLOGICAL_STATES[0];
  }
  return [...CHRONOLOGICAL_STATES]
    .filter((state) => hasState(d, state))
    .sort((a, b) => getStateTimestamp(d, b) - getStateTimestamp(d, a))[0];
}

/**
 * The user-facing state. Prefers an end-user engagement state; otherwise
 * collapses the drafted/queued+sent(+delivered) chronology to sent/delivered,
 * else falls back to the latest chronological state. Ported verbatim from the
 * Ember `displayState` getter.
 */
export function getDisplayState(d: Delivery): DeliveryState | undefined {
  const possibleStates = isEmail(d) ? END_USER_STATES_EMAIL : END_USER_STATES;
  const endUserState = possibleStates.find((state) => hasState(d, state));
  if (endUserState != null) {
    return endUserState;
  }

  const latest = getLatestChronologicalState(d);
  const latestIsDraftOrQueued = latest === 'drafted' || latest === 'queued';
  const sent = hasState(d, 'sent');
  const delivered = hasState(d, 'delivered');

  if (latest == null || (latestIsDraftOrQueued && sent && !delivered)) {
    return 'sent';
  }
  if (latestIsDraftOrQueued && sent && delivered) {
    return 'delivered';
  }
  return latest;
}

/**
 * The timestamp (unix seconds) of the delivery's current display state — Ember
 * `displayStateTimestamp` (`this[this.displayState]`). Drives the preview
 * header's state timestamp. Returns 0 when there is no display state.
 */
export function getDisplayStateTimestamp(d: Delivery): number {
  const state = getDisplayState(d);
  if (state == null) return 0;
  const value = getStateTimestamp(d, state);
  // The engagement states read the opaque `metrics` map, which mixes counts and
  // timestamps, so a count-shaped value here would render as 1 Jan 1970. Report it as
  // absent so the header shows the state tag without a bogus date. (Ember renders the
  // epoch date in this case; the repo's own metric reads all bounds-check instead.)
  if (isMetricSourcedState(state) && !isDeliveryMetricTimestamp(value, d.created)) {
    return 0;
  }
  return value;
}

/**
 * Ember `errorMessage`: the first present of the delivery's `failure_message`
 * or its `reason` / `failed_message` meta values.
 */
export function getErrorMessage(d: Delivery): string {
  const candidates = [d.failure_message, metaValue(d, 'reason'), metaValue(d, 'failed_message')];
  return candidates.find((value) => !isEmptyString(value)) ?? '';
}

/** Ember `hasError`: an error on the delivery, or error messages on its rendered detail. */
export function hasDeliveryError(d: Delivery, detail: Pick<DeliveryDetail, 'error_messages'> | null): boolean {
  return !isEmptyString(getErrorMessage(d)) || (detail?.error_messages ?? []).length > 0;
}

/** Redeliverable when the current state is sent/suppressed/bounced/spammed/delivered. */
export function isRedeliverable(d: Delivery): boolean {
  const state = getDeliveryState(d);
  return (
    state === 'sent' || state === 'suppressed' || state === 'bounced' || state === 'spammed' || state === 'delivered'
  );
}

function isPendingOp(d: Delivery): boolean {
  return d.pendingDelete === true || d.pendingRetry === true || d.pendingSend === true;
}

/**
 * Whether a delivery can be (re)sent or deleted — the gate for optimistic bulk
 * marking. Never sent, no op in flight, and in a failed/drafted/undeliverable
 * state.
 */
export function canBeSentOrDeleted(d: Delivery): boolean {
  return (
    !hasState(d, 'sent') &&
    !isPendingOp(d) &&
    (hasState(d, 'failed') || hasState(d, 'drafted') || hasState(d, 'undeliverable'))
  );
}

/* ------------------------------------------------------------------------- */
/* Request shaping (ported from app/controllers/deliveries.js)               */
/* ------------------------------------------------------------------------- */

interface CampaignIdFilter {
  campaign_id?: number;
  newsletter_id?: number;
  template_id?: number;
  transactional_message_id?: number;
}

/**
 * Split a campaign filter value into the right typed id (Ember prefix rules).
 * The endpoints type these ids as numbers, so parse. Shared by the list-param
 * and export-body builders.
 */
function parseCampaignFilter(campaign: string): CampaignIdFilter {
  if (campaign.startsWith('newsletter_')) {
    return { newsletter_id: Number(campaign.replace('newsletter_', '')) };
  }
  if (campaign.startsWith('template_')) {
    return { template_id: Number(campaign.replace('template_', '')) };
  }
  if (campaign.startsWith('transactional_')) {
    return { transactional_message_id: Number(campaign.replace('transactional_', '')) };
  }
  return { campaign_id: Number(campaign) };
}

/** Export date range overrides the list range only when both bounds are set. */
function resolveExportRange(input: DeliveryExportInput): { start_ts: number; end_ts: number } {
  if (input.exportStartTs != null && input.exportEndTs != null) {
    return { start_ts: input.exportStartTs, end_ts: input.exportEndTs };
  }
  return { start_ts: input.startTs ?? 0, end_ts: input.endTs ?? 0 };
}

const DELIVERY_TYPES = [
  '',
  'all',
  'email',
  'in_app',
  'inbox',
  'line',
  'live_notification',
  'push',
  'slack',
  'sms',
  'twilio',
  'urban_airship',
  'webhook',
  'whatsapp',
] as const satisfies readonly DeliveryType[];

export function deliveryType(value: string | undefined): DeliveryType | undefined {
  return DELIVERY_TYPES.find((type) => type === value);
}

export interface DeliveryFilterInput {
  recipient?: string;
  personId?: string;
  drafts?: boolean;
  deliveryStatus?: string;
  startTs?: number;
  endTs?: number;
  actionType?: string;
  campaign?: string;
  action?: string;
  trigger?: string;
}

/** Strip empty (`undefined`/`null`/`''`) values so they aren't sent as blank query params. */
function stripEmpty<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined || value === null || value === '') continue;
    out[key] = value;
  }
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return out as Partial<T>;
}

/**
 * Build the deliveries list query params (port of `getDeliveryQuery`).
 * `action_id` wins over any campaign filter; empty params are dropped.
 */
export function buildDeliveriesListParams(input: DeliveryFilterInput): DeliveriesListParams {
  const params: DeliveriesListParams = {
    recipient: input.recipient,
    internal_id: input.personId,
    drafts: input.drafts,
    metric: input.deliveryStatus,
    start_ts: input.startTs ?? 0,
    end_ts: input.endTs ?? 0,
    type: deliveryType(input.actionType),
  };

  // The trigger narrows a message filter too: the endpoint reads both together.
  if (input.trigger != null && input.trigger !== '') {
    params.trigger_id = input.trigger;
  }
  if (input.action == null || input.action === '') {
    if (input.campaign != null && input.campaign !== '') {
      Object.assign(params, parseCampaignFilter(input.campaign));
    }
  } else {
    params.action_id = input.action;
  }

  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return stripEmpty(params as Record<string, unknown>) as DeliveriesListParams;
}

export interface DeliveryExportInput {
  deliveryStatus?: string;
  actionType?: string;
  startTs?: number;
  endTs?: number;
  exportStartTs?: number;
  exportEndTs?: number;
  campaign?: string;
  action?: string;
  drafted?: boolean;
  /** "<campaignId>-<triggerId>" — only the trigger part is sent. */
  broadcastTrigger?: string;
}

/** Shape of the `POST exports/deliveries` body (subset of `ExportsDeliveriesRequest`). */
export interface DeliveriesExportBody {
  start_ts: number;
  end_ts: number;
  metric?: string;
  delivery_type: string;
  campaign_id?: number;
  newsletter_id?: number;
  template_id?: number;
  transactional_message_id?: number;
  action_id: string | null;
  drafted?: boolean;
  trigger_id?: number;
  attributes: string[];
  exported_from?: string;
}

/**
 * Build the deliveries export body — port of `serializeDeliveriesFilters` +
 * the `export()` additions (`attributes: ['email']`, `exported_from`). Export
 * date range overrides the list range when both bounds are set; a drafted
 * export drops the `metric` filter.
 */
export function buildDeliveriesExportBody(input: DeliveryExportInput, from?: string): DeliveriesExportBody {
  const body: DeliveriesExportBody = {
    ...resolveExportRange(input),
    metric: input.deliveryStatus ?? '',
    delivery_type: input.actionType ?? '',
    action_id: input.action != null && input.action !== '' ? input.action : null,
    attributes: ['email'],
  };

  if (input.campaign != null && input.campaign !== '') {
    Object.assign(body, parseCampaignFilter(input.campaign));
  }

  if (input.drafted === true) {
    body.drafted = true;
    delete body.metric;
  }

  if (input.broadcastTrigger != null && input.broadcastTrigger !== '') {
    body.trigger_id = Number(input.broadcastTrigger.split('-')[1]);
  }

  if (from != null && from !== '') {
    body.exported_from = from;
  }

  return body;
}
